// Montageservice cart toggle — lets customers add the Montageservice to a product
// straight from the cart (page + drawer), without going back to the product page.
//
// On toggle-on it:
//   1. reads the live parent line (to keep its existing properties + quantity),
//   2. tags the parent with a hidden _montage_pair_id via /cart/change.js
//      (properties are replaced wholesale, so we merge the existing ones back in),
//   3. adds the resolved Montageservice variant as a coupled addon line via /cart/add.js
//      (carrying the same _montage_pair_id + _is_montage_addon marker),
//   4. re-renders the cart section(s), after which cart-products.liquid draws the paired
//      "+" line and this toggle disappears.
//
// Removing the montage line again uses the existing "Verwijderen" link on the paired row;
// the toggle then reappears (and a fresh pair id is generated on the next add).
import { CartUpdateEvent } from "@theme/events";

const PAIR_KEY = "_montage_pair_id";
const ADDON_KEY = "_is_montage_addon";

function getRoutes() {
  const r = (window.MorgennTheme && window.MorgennTheme.routes) || {};
  return {
    cart: r.cart || "/cart.js",
    cartAdd: r.cart_add_url || "/cart/add.js",
    cartChange: r.cart_change_url || "/cart/change.js",
  };
}

function collectSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll("cart-items-component[data-section-id], cart-drawer-component[data-section-id]")
    .forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
  return Array.from(ids);
}

function generatePairId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

class MontageCartToggleComponent extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    if (!this.checkbox) return;
    this._onChange = this.onChange.bind(this);
    this.checkbox.addEventListener("change", this._onChange);
  }

  disconnectedCallback() {
    if (this.checkbox && this._onChange) {
      this.checkbox.removeEventListener("change", this._onChange);
    }
  }

  async onChange() {
    // The toggle only ever appears when there is no montage yet, so we only act on turn-on.
    if (!this.checkbox.checked) return;
    if (this._busy) return;
    this._busy = true;
    this.classList.add("is-loading");

    const assemblyVariantId = this.dataset.assemblyVariantId;
    const parentKey = this.dataset.parentKey;
    if (!assemblyVariantId || !parentKey) {
      this.#reset();
      return;
    }

    const routes = getRoutes();
    const sectionIds = collectSectionIds();
    const sectionsParam = sectionIds.length > 0 ? sectionIds.join(",") : null;

    try {
      // 1. Read the live parent line so we keep its existing properties + quantity.
      const cartRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
      const cart = cartRes.ok ? await cartRes.json() : null;
      const parent = cart && cart.items && cart.items.find((it) => it.key === parentKey);
      if (!parent) {
        this.#reset();
        return;
      }

      const pairId = generatePairId();
      const quantity = parent.quantity || 1;

      // 2. Tag the parent with the coupling id (properties are replaced, so merge existing back in).
      const mergedProps = Object.assign({}, parent.properties || {});
      mergedProps[PAIR_KEY] = pairId;

      const changeBody = { id: parentKey, quantity, properties: mergedProps };
      if (sectionsParam) changeBody.sections = sectionsParam;

      const changeRes = await fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(changeBody),
      });
      if (!changeRes.ok) throw new Error("cart/change failed: " + changeRes.status);

      // 3. Add the Montageservice as a coupled addon line (matching quantity).
      const fd = new FormData();
      fd.append("id", assemblyVariantId);
      fd.append("quantity", String(quantity));
      fd.append(`properties[${PAIR_KEY}]`, pairId);
      fd.append(`properties[${ADDON_KEY}]`, "1");
      if (sectionsParam) fd.append("sections", sectionsParam);

      const addRes = await fetch(routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      if (!addRes.ok) throw new Error("cart/add failed: " + addRes.status);
      const addData = await addRes.json();

      // 4. Re-render the cart section(s) with the new paired line (absolute item count).
      const freshRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
      const freshCart = freshRes.ok ? await freshRes.json() : {};
      document.dispatchEvent(
        new CartUpdateEvent(freshCart, this.id || "montage-cart-toggle", {
          itemCount: freshCart.item_count,
          source: "montage-cart-toggle",
          variantId: assemblyVariantId,
          sections: addData.sections,
        })
      );
    } catch (err) {
      console.error("[montage-cart-toggle]", err);
      this.#reset();
    }
  }

  // On failure, flip the switch back off so the customer can retry.
  #reset() {
    this._busy = false;
    this.classList.remove("is-loading");
    if (this.checkbox) this.checkbox.checked = false;
  }
}

if (!customElements.get("montage-cart-toggle-component")) {
  customElements.define("montage-cart-toggle-component", MontageCartToggleComponent);
}
